<template>
    <div>
        <template v-if="!isAuth">
            <a :href="'/register?return=' + quickClass.uri" class="btn btn-primary btn-sm" style="display: inline-flex; align-items: center;">
                <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
                     viewBox="0 0 512 512" xml:space="preserve" fill="currentColor" style="width: 1em; height: 1em; vertical-align: middle; margin-right: 0.2em;">
                    <g>
                        <g>
                            <path d="M70.715,0v512L256,326.715L441.285,512V0H70.715z M411.239,439.462L256,284.224L100.761,439.462V30.046h310.477V439.462z"/>
                        </g>
                    </g>
                </svg>
                Save
            </a>
        </template>
        <template v-else>
            <!-- Button to open the modal -->
            <b-button 
                :variant="isInAnyPlaylist ? 'success' : 'primary'" 
                class="btn-sm" 
                @click="openModal"
                :disabled="initialLoading"
            >
                <template v-if="initialLoading">
                    <b-spinner small></b-spinner>
                </template>
                <template v-else>
                    <svg version="1.1" id="Layer_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
                         viewBox="0 0 512 512" xml:space="preserve" fill="currentColor" style="width: 1em; height: 1em; vertical-align: middle; margin-right: 0.2em;">
                        <g>
                            <g>
                                <path d="M70.715,0v512L256,326.715L441.285,512V0H70.715z M411.239,439.462L256,284.224L100.761,439.462V30.046h310.477V439.462z"/>
                            </g>
                        </g>
                    </svg>
                    {{ isInAnyPlaylist ? 'Saved' : 'Save' }}
                </template>
            </b-button>
        </template>

        <!-- Modal Dialog (Always Rendered) -->
        <b-modal v-model="showModal" title="Save class to..." @shown="fetchPlaylists" centered>
            <!-- Loading Indicator -->
            <div v-if="loading" class="text-center my-3">
                <b-spinner label="Loading..."></b-spinner>
            </div>

            <!-- Playlists and Actions -->
            <div v-else>
                <!-- List of Playlists -->
                <ul class="list-group mb-3 pl-0" :class="{ 'border-bottom pb-3': playlists.length !== 0 }">
                    <li
                        v-for="playlist in playlists"
                        :key="playlist.id"
                        class="list-group-item d-flex justify-content-between align-items-center mb-2"
                    >
                        <span>{{ playlist.name }}</span>
                        <div>
                            <!-- Add or Remove Button -->
                            <b-button
                                :variant="isQuickClassInPlaylist(playlist) ? 'danger' : 'success'"
                                size="sm"
                                @click="toggleQuickClassInPlaylist(playlist)"
                            >
                                {{ isQuickClassInPlaylist(playlist) ? 'Remove' : 'Add' }}
                            </b-button>
                        </div>
                    </li>
                </ul>

                <!-- Create Playlist Button -->
                <div v-if="!showCreatePlaylistForm" class="text-center">
                    <b-button variant="link" @click="toggleCreatePlaylistForm">
                        Create Playlist
                    </b-button>
                </div>

                <!-- Create New Playlist Form -->
                <transition name="slide-down" @after-enter="onCreatePlaylistFormEntered">
                    <div v-if="showCreatePlaylistForm" class="mt-3">
                        <form @submit.prevent="createPlaylist">
                            <div class="form-group">
                                <label for="new-playlist-name">Create New Playlist</label>
                                <div class="input-group">
                                    <input
                                        type="text"
                                        id="new-playlist-name"
                                        ref="new-playlist-name"
                                        v-model="newPlaylistName"
                                        placeholder="Enter playlist name"
                                        required
                                        class="form-control"
                                    />
                                    <div class="input-group-append">
                                        <button type="submit" class="btn btn-primary">Create</button>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </transition>
            </div>
        </b-modal>
    </div>
</template>

<script>
export default {
    props: {
        quickClass: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            showModal: false,
            loading: false,
            initialLoading: true,
            playlists: [],
            showCreatePlaylistForm: false,
            newPlaylistName: '',
            isInAnyPlaylist: false,
            isAuth: false,
            user: {},
        };
    },

    mounted() {
        // Check if the user is logged in
        if (window.virtuoso && window.virtuoso.is_auth) {
            this.isAuth = true;
            this.user = window.virtuoso.user;
            this.fetchPlaylists(); // Fetch playlists immediately when component mounts
        }
    },

    methods: {
        openModal() {
            this.showModal = true;
            this.showCreatePlaylistForm = false; // Reset form visibility when modal opens
        },

        toggleCreatePlaylistForm() {
            this.showCreatePlaylistForm = !this.showCreatePlaylistForm;
        },

        onCreatePlaylistFormEntered() {
            if (this.$refs['new-playlist-name']) {
                this.$refs['new-playlist-name'].focus();
            }
        },

        async fetchPlaylists() {
            try {
                const response = await axios.get('/api/me/playlists?type=quickclasses');
                this.playlists = response.data;
                // Check if the quick class is in any playlist
                this.isInAnyPlaylist = this.playlists.some(playlist => 
                    playlist.quick_classes.some(qc => qc.id === this.quickClass.id)
                );
            } catch (error) {
                console.error('Error fetching playlists:', error);
                alert('Failed to load playlists.');
            } finally {
                this.initialLoading = false;
            }
        },

        isQuickClassInPlaylist(playlist) {
            return playlist.quick_classes.some(
                (qc) => qc.id === this.quickClass.id
            );
        },

        async toggleQuickClassInPlaylist(playlist) {
            try {
                if (this.isQuickClassInPlaylist(playlist)) {
                    // Remove quick class from playlist
                    await axios.post(`/api/me/playlists/${playlist.id}/remove?type=quickclasses`, {
                        entity_id: this.quickClass.id,
                    });
                    // Update the playlist data locally
                    playlist.quick_classes = playlist.quick_classes.filter(
                        (qc) => qc.id !== this.quickClass.id
                    );
                } else {
                    // Add quick class to playlist
                    await axios.post(`/api/me/playlists/${playlist.id}/add?type=quickclasses`, {
                        entity_id: this.quickClass.id,
                    });
                    // Update the playlist data locally
                    playlist.quick_classes.push({ id: this.quickClass.id });
                }
                // Update the isInAnyPlaylist flag
                this.isInAnyPlaylist = this.playlists.some(playlist => 
                    playlist.quick_classes.some(qc => qc.id === this.quickClass.id)
                );
            } catch (error) {
                console.error('Error updating playlist:', error);
                alert('Failed to update playlist.');
            }
        },

        async createPlaylist() {
            if (!this.newPlaylistName.trim()) {
                return;
            }
            try {
                const response = await axios.post('/api/me/playlists?type=quickclasses', {
                    name: this.newPlaylistName,
                    entity_id: this.quickClass.id,
                });
                // Add the new playlist to the list
                this.playlists.push(response.data);
                // Reset the new playlist name
                this.newPlaylistName = '';
                // Hide the form after creation
                this.showCreatePlaylistForm = false;
                // Update the isInAnyPlaylist flag
                this.isInAnyPlaylist = true;
            } catch (error) {
                console.error('Error creating playlist:', error);
                alert('Failed to create playlist.');
            }
        },
    },
};
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s ease;
    overflow: hidden;
}
.slide-down-enter,
.slide-down-leave-to {
    max-height: 0;
    opacity: 0;
}
.slide-down-enter-to,
.slide-down-leave {
    max-height: 500px; /* Adjust as needed */
    opacity: 1;
}
</style>
